import { push } from "connected-react-router";
import PropTypes from "prop-types";
import React, { Component } from "react";
import DocumentTitle from "react-document-title";
import { connect } from "react-redux";

import ListViewTable from "../../listview/components/ListViewTable";
import { ListTablePolicyShape } from "../../listview/proptypes";
import listTablePoliciesSelectors from "../../listview/selectors";
import { userSelectors } from "../../nav/selectors";
import {
  groups,
  resourceTypes,
  roles,
  services,
  users
} from "../../policies/actions";
import {
  EntityShape,
  GroupShape,
  LoggedInUserShape,
  UserShape
} from "../../policies/proptypes";
import policySelectors from "../../policies/selectors";
import withModal from "../../shared/components/withModal";
import {
  MIN_WIDTH_RESOURCES,
  MIN_WIDTH_ROLES,
  MIN_WIDTH_SERVICES
} from "../../shared/constants";
import GroupsActions from "../components/GroupsActions";
import GroupsList from "../components/GroupsList";
import GroupUsers from "../components/GroupUsers";
import UserDetails from "../components/UserDetails";
import RemoveEntity from "./RemoveEntity";

export class Groups extends Component {
  title = "RBAC - Groups/Users";

  state = {
    columns: [
      { label: "Services", key: "service", minWidth: MIN_WIDTH_SERVICES },
      { label: "Scopes", key: "resource", minWidth: MIN_WIDTH_RESOURCES },
      { label: "Roles", key: "role", minWidth: MIN_WIDTH_ROLES }
    ],
    orderBy: ["service", "resource", "role"],
    sortBy: "group",
    removeUserDialogVisible: false,
    removeGroupDialogVisible: false
  };

  static getDerivedStateFromProps = (props, state) => {
    if (state.removeUserDialogVisible && !props.currentUser) {
      return { removeUserDialogVisible: false };
    }

    if (state.removeGroupDialogVisible && !props.currentGroup) {
      return { removeGroupDialogVisible: false };
    }

    return null;
  };

  componentDidMount() {
    const { matchedEntity } = this.props;
    if (matchedEntity) {
      this.setCurrentEntity(matchedEntity);
    }
  }

  componentDidUpdate(prevProps) {
    const { matchedEntity } = this.props;
    if (matchedEntity && matchedEntity !== prevProps.matchedEntity) {
      this.setCurrentEntity(matchedEntity);
    }
  }

  setCurrentEntity = entity => {
    const { setCurrentUser, setCurrentGroup } = this.props;
    if (entity.type === "group") {
      setCurrentUser(undefined);
      setCurrentGroup(entity.$uri);
    }
    if (entity.type === "user") {
      setCurrentGroup(undefined);
      setCurrentUser(entity.$uri);
    }
  };

  toggleRemoveUserVisibility = () => {
    const { removeUserDialogVisible } = this.state;
    this.setState({ removeUserDialogVisible: !removeUserDialogVisible });
  };

  toggleRemoveGroupVisibility = () => {
    const { removeGroupDialogVisible } = this.state;
    this.setState({ removeGroupDialogVisible: !removeGroupDialogVisible });
  };

  hideRemoveDialogs = () => {
    this.setState({
      removeUserDialogVisible: false,
      removeGroupDialogVisible: false
    });
  };

  getSortedEntities = () => {
    const { sortBy } = this.state;
    const {
      entitiesByGroup,
      entitiesByUser,
      entitiesByName,
      entitiesByDomain
    } = this.props;
    switch (sortBy) {
      case "group":
        return entitiesByGroup;
      case "user":
        return entitiesByUser;
      case "name":
        return entitiesByName;
      case "domain":
        return entitiesByDomain;
      default:
        return entitiesByGroup;
    }
  };

  setSortState = option => {
    this.setState({ sortBy: option });
  };

  openRoles = uri => {
    const { setCurrentRole, push } = this.props;
    setCurrentRole(uri);
    push("/policies/roles");
  };

  openResources = uri => {
    const { setCurrentResourceType, push } = this.props;
    setCurrentResourceType(uri);
    push("/policies/scopes");
  };

  render() {
    const {
      columns,
      orderBy,
      sortBy,
      removeUserDialogVisible,
      removeGroupDialogVisible
    } = this.state;
    const {
      policies,
      currentGroup,
      currentGroupUsers,
      currentUser,
      loggedInUser,
      canEditUsers,
      parentUrl
    } = this.props;

    const self = currentUser ? loggedInUser.$uri === currentUser.$uri : false;
    const entities = this.getSortedEntities();
    const removeDisabled = () => self || !canEditUsers;

    return (
      <div className="l-groups">
        <DocumentTitle title="RBAC - Groups/Users" />
        <GroupsList
          entities={entities}
          currentGroup={currentGroup}
          currentUser={currentUser}
          sortBy={sortBy}
          setSortState={this.setSortState}
          onItemSelect={this.hideRemoveDialogs}
        />
        <div
          className="l-groups__column"
          style={{ gridArea: "entity-policies" }}
        >
          <ListViewTable
            columns={columns}
            orderBy={orderBy}
            policies={policies}
            openRoles={this.openRoles}
            openResources={this.openResources}
            showActions={false}
            parentUrl={parentUrl}
          />
        </div>
        {currentGroup && (
          <GroupUsers
            group={currentGroup}
            users={currentGroupUsers}
            parentUrl={parentUrl}
          />
        )}
        {currentUser && <UserDetails user={currentUser} />}
        <GroupsActions
          disabled={removeDisabled()}
          removeUser={currentUser && this.toggleRemoveUserVisibility}
          removeGroup={currentGroup && this.toggleRemoveGroupVisibility}
          parentUrl={parentUrl}
        />
        {removeUserDialogVisible && (
          <RemoveEntity
            user={currentUser}
            cancel={this.toggleRemoveUserVisibility}
          />
        )}
        {removeGroupDialogVisible && (
          <RemoveEntity
            group={currentGroup}
            cancel={this.toggleRemoveGroupVisibility}
          />
        )}
      </div>
    );
  }
}

Groups.defaultProps = {
  currentGroup: undefined,
  currentUser: undefined,
  matchedEntity: undefined
};

Groups.propTypes = {
  parentUrl: PropTypes.string.isRequired,
  entitiesByGroup: PropTypes.arrayOf(EntityShape).isRequired,
  entitiesByUser: PropTypes.arrayOf(EntityShape).isRequired,
  entitiesByName: PropTypes.arrayOf(EntityShape).isRequired,
  entitiesByDomain: PropTypes.arrayOf(EntityShape).isRequired,
  policies: PropTypes.arrayOf(ListTablePolicyShape).isRequired,
  currentGroup: GroupShape,
  currentGroupUsers: PropTypes.arrayOf(UserShape).isRequired,
  currentUser: UserShape,
  loggedInUser: LoggedInUserShape.isRequired,
  setCurrentGroup: PropTypes.func.isRequired,
  setCurrentResourceType: PropTypes.func.isRequired,
  setCurrentRole: PropTypes.func.isRequired,
  setCurrentUser: PropTypes.func.isRequired,
  canEditUsers: PropTypes.bool.isRequired,
  push: PropTypes.func.isRequired,
  matchedEntity: EntityShape
};

const mapStateToProps = (state, props) => ({
  entitiesByGroup: policySelectors.entities.getByGroup(state),
  entitiesByUser: policySelectors.entities.getByUser(state),
  entitiesByName: policySelectors.entities.getByName(state),
  entitiesByDomain: policySelectors.entities.getByDomain(state),
  policies: listTablePoliciesSelectors.getByCurrentEntity(state),
  currentGroup: policySelectors.groups.getCurrent(state),
  currentGroupUsers: policySelectors.users.getByCurrentGroup(state),
  currentUser: policySelectors.users.getCurrent(state),
  loggedInUser: state.user,
  canEditUsers: userSelectors.canEditUsers(state),
  matchedEntity: policySelectors.entities.getMatched(state, props)
});

const mapDispatchToProps = {
  setCurrentGroup: groups.setCurrent,
  setCurrentResourceType: resourceTypes.setCurrent,
  setCurrentRole: roles.setCurrent,
  setCurrentService: services.setCurrent,
  setCurrentUser: users.setCurrent,
  push
};

export default withModal(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(Groups)
);
